/**
 * Where are we? (docs/mockups/install.html, Spec panel 2.) A pure read of the browser:
 * the user agent and a few navigator/screen facts go in, a `PlatformInfo` comes out, so
 * every rule here is tested without a browser. `readPlatform()` reads the real one.
 */
import inAppData from './in-app-browsers.json';

export interface InAppApp {
	id: string;
	/** As it reads in "You can't add apps from inside {name}". */
	name: string;
	/** The menu item after the app's ⋯ (iPhone) or ⋮ (Android). */
	menu: { ios: string; android: string };
}

interface InAppEntry extends InAppApp {
	pattern: string;
}

/** The in-app browser list (in-app-browsers.json), compiled once. A bad pattern is skipped. */
const IN_APP: { app: InAppApp; re: RegExp }[] = (inAppData.apps as InAppEntry[]).flatMap(({ pattern, ...app }) => {
	try {
		return [{ app, re: new RegExp(pattern) }];
	} catch {
		return [];
	}
});
const TELEGRAM: InAppApp = inAppData.telegram;

/** The `platform` prop every install event carries (Spec panel 3). */
export type AnalyticsPlatform = 'android' | 'ios_safari' | 'ios_other' | 'in_app' | 'desktop';

export type IosBrowser = 'safari' | 'chrome' | 'firefox' | 'edge' | 'in_app';
/** Which Safari bar the iPhone guide draws: iOS 26 compact (⋯), the older toolbar (Share), the iPad's top bar. */
export type IosBar = 'compact_more' | 'toolbar_share' | 'ipad_top';
export type AndroidBrowser = 'chrome' | 'samsung' | 'firefox' | 'other';

export interface PlatformInfo {
	/** Opened from the home screen (display-mode standalone, or iOS's navigator.standalone). */
	standalone: boolean;
	ios: boolean;
	android: boolean;
	/** An app's own browser (Instagram, Facebook…): nothing can be installed from inside it. */
	inApp: InAppApp | null;
	iosBrowser: IosBrowser | null;
	iosBar: IosBar | null;
	androidBrowser: AndroidBrowser | null;
	/** Safari on a Mac (File → Add to Dock). */
	macSafari: boolean;
	/** Not a phone or tablet. */
	desktop: boolean;
	analytics: AnalyticsPlatform;
}

export interface PlatformInput {
	ua: string;
	/** `navigator.platform` (iPadOS reports MacIntel). */
	navPlatform?: string;
	maxTouchPoints?: number;
	screenWidth?: number;
	/** `matchMedia('(display-mode: standalone)').matches` (or fullscreen / minimal-ui). */
	displayStandalone?: boolean;
	/** iOS's `navigator.standalone`. */
	navigatorStandalone?: boolean;
	/** Telegram's in-app browser (it doesn't mark the UA): `window.TelegramWebviewProxy` exists. */
	telegram?: boolean;
}

export function detectPlatform(input: PlatformInput): PlatformInfo {
	const { ua } = input;
	const touch = input.maxTouchPoints ?? 0;
	const standalone = input.displayStandalone === true || input.navigatorStandalone === true;
	const ipadOs = input.navPlatform === 'MacIntel' && touch > 1;
	const ios = /iPhone|iPad|iPod/.test(ua) || ipadOs;
	const android = !ios && /Android/.test(ua);
	const inApp = input.telegram ? TELEGRAM : (IN_APP.find(({ re }) => re.test(ua))?.app ?? null);

	let iosBrowser: IosBrowser | null = null;
	let iosBar: IosBar | null = null;
	if (ios) {
		iosBrowser = inApp ? 'in_app' : /CriOS/.test(ua) ? 'chrome' : /FxiOS/.test(ua) ? 'firefox' : /EdgiOS/.test(ua) ? 'edge' : 'safari';
		// iOS 26 froze the OS version in the UA; Safari's own version still moves.
		const safariMajor = Number(/Version\/(\d+)/.exec(ua)?.[1] ?? 0);
		const ipad = /iPad/.test(ua) || ipadOs || (touch > 1 && (input.screenWidth ?? 0) >= 744);
		iosBar = ipad ? 'ipad_top' : safariMajor >= 26 ? 'compact_more' : 'toolbar_share';
	}
	const androidBrowser: AndroidBrowser | null = android
		? /SamsungBrowser/.test(ua)
			? 'samsung'
			: /Firefox/.test(ua)
				? 'firefox'
				: /Chrome\//.test(ua) && !/EdgA|OPR|YaBrowser|UCBrowser|DuckDuckGo/.test(ua)
					? 'chrome'
					: 'other'
		: null;
	const mobile = ios || android || /Mobi/.test(ua);
	const macSafari = !mobile && /Macintosh/.test(ua) && /Safari\//.test(ua) && /Version\//.test(ua) && !/Chrome|Chromium|Edg\//.test(ua);
	const analytics: AnalyticsPlatform = inApp ? 'in_app' : ios ? (iosBrowser === 'safari' ? 'ios_safari' : 'ios_other') : android ? 'android' : 'desktop';
	return { standalone, ios, android, inApp, iosBrowser, iosBar, androidBrowser, macSafari, desktop: !mobile, analytics };
}

/**
 * What our "Add to home screen" button does here (Spec panel 2, "The card's button"):
 * - `native`: call the held `beforeinstallprompt` (Android Chrome, desktop Chrome / Edge);
 * - `way_out`: an in-app browser: out to Safari / Chrome first;
 * - `ios_guide`: Safari (or Chrome / Edge / Firefox) on iPhone or iPad: the three-step guide;
 * - `android_guide`: Android without a prompt (Samsung Internet, Firefox, a Custom Tab): the ⋮ guide;
 * - `mac_safari`: Safari on a Mac: File → Add to Dock (the menu line only, never a card);
 * - null: nothing to offer (standalone, desktop without a prompt, Firefox on a computer).
 */
export type InstallRoute = 'native' | 'way_out' | 'ios_guide' | 'android_guide' | 'mac_safari';

export function installRoute(p: PlatformInfo, held: boolean): InstallRoute | null {
	if (p.standalone) return null;
	if (p.inApp) return 'way_out';
	if (held) return 'native';
	if (p.ios) return 'ios_guide';
	if (p.android) return 'android_guide';
	if (p.macSafari) return 'mac_safari';
	return null;
}

/** The real browser, read defensively (never throws). */
export function readPlatform(): PlatformInfo {
	if (typeof window === 'undefined' || typeof navigator === 'undefined') return detectPlatform({ ua: '' });
	const media = (q: string) => {
		try {
			return window.matchMedia?.(q).matches === true;
		} catch {
			return false;
		}
	};
	return detectPlatform({
		ua: navigator.userAgent ?? '',
		navPlatform: navigator.platform,
		maxTouchPoints: navigator.maxTouchPoints,
		screenWidth: window.screen?.width,
		displayStandalone: media('(display-mode: standalone)') || media('(display-mode: fullscreen)') || media('(display-mode: minimal-ui)'),
		navigatorStandalone: (navigator as Navigator & { standalone?: boolean }).standalone === true,
		telegram: 'TelegramWebviewProxy' in window,
	});
}
